import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [settings, setSettings] = useState({ totp_enforced: false, ip_whitelist_enforced: false });
  const [demoMode, setDemoModeState] = useState(() => localStorage.getItem("demo_mode") === "1");
  const [demoAvailable, setDemoAvailable] = useState(false);

  const setDemoMode = useCallback((v) => {
    if (v) localStorage.setItem("demo_mode", "1");
    else { localStorage.removeItem("demo_mode"); localStorage.removeItem("impersonate_id"); }
    setDemoModeState(!!v);
  }, []);

  const impersonate = useCallback(async (userId) => {
    if (userId) localStorage.setItem("impersonate_id", userId);
    else localStorage.removeItem("impersonate_id");
    // Re-fetch me so context updates to reflect the new effective user
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_) { /* ignore */ }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // /auth/me now returns { ...user, settings: { totp_enforced, ip_whitelist_enforced } }
      const { settings: bundledSettings, ...userData } = data;
      setUser(userData);
      if (bundledSettings) {
        setSettings((s) => ({ ...s, ...bundledSettings }));
      }
      // Probe demo status for admin — used to show/hide the demo toggle
      if (userData.role === "admin") {
        try {
          const ds = await api.get("/admin/demo/status");
          setDemoAvailable((ds.data.users || 0) + (ds.data.payments || 0) > 0);
        } catch (_) { /* ignore — enroll token can't hit /admin */ }
      }
      return userData;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async ({ email, password, totp_code }) => {
    const { data } = await api.post("/auth/login", { email, password, totp_code });
    if (data.requires_totp) return { requires_totp: true };
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.user) setUser(data.user);
    if (data.settings) setSettings((s) => ({ ...s, ...data.settings }));
    if (data.requires_totp_setup) {
      // User is logged in with a token but must complete TOTP setup before doing anything
      return { ok: true, user: data.user, requires_totp_setup: true };
    }
    return { ok: true, user: data.user };
  };

  // Once user is loaded (either via fetchMe or login), attempt to subscribe to
  // push notifications. Fires-and-forgets — errors are swallowed inside the
  // helper so we never block the UI.
  useEffect(() => {
    if (user && typeof user === "object" && user.totp_enabled !== false) {
      import("./push").then((m) => m.ensurePushSubscription({ prompt: true })).catch(() => null);
    }
  }, [user]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    localStorage.removeItem("access_token");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, settings, setSettings, login, logout, refresh: fetchMe, formatApiErrorDetail, demoMode, setDemoMode, demoAvailable, setDemoAvailable, impersonate }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
